import { FiFileText } from 'react-icons/fi';

export const essayStructureData = {
  courseId: 'task2-essay-structure',
  courseType: 'writing-course',
  title: 'Essay Structure',
  icon: FiFileText,
  overview: {
    impact: 'Essential framework for Band 7+ essays',
    focus: 'Organizing Task 2 essays effectively',
    uses: 'IELTS Writing Task 2',
    duration: '30 minutes',
  },
  sections: [
    {
      title: 'Task 2 Essay Types',
      content: `<p>Know the main essay types and their structures:</p>`,
      table: {
        headers: ['Essay Type', 'Question Prompt', 'Structure'],
        rows: [
          ['Opinion', '"Do you agree or disagree?"', 'Intro + 2-3 supporting paragraphs + Conclusion'],
          ['Discussion', '"Discuss both views and give your opinion"', 'Intro + View 1 + View 2 + Opinion + Conclusion'],
          ['Problem/Solution', '"What problems and solutions..."', 'Intro + Problems + Solutions + Conclusion'],
          ['Advantages/Disadvantages', '"What are the pros and cons?"', 'Intro + Advantages + Disadvantages + Conclusion'],
        ],
      },
    },
    {
      title: 'The 4-Paragraph Structure',
      content: `<p>This structure works for most essay types:</p>`,
      points: [
        '<strong>Introduction</strong> (2-3 sentences): Hook + Background + Thesis',
        '<strong>Body Paragraph 1</strong>: Topic sentence + Explanation + Example',
        '<strong>Body Paragraph 2</strong>: Topic sentence + Explanation + Example',
        '<strong>Conclusion</strong> (2-3 sentences): Restate thesis + Final thought',
      ],
    },
    {
      title: 'Writing Strong Introductions',
      content: `<p>A good introduction has three elements:</p>`,
      points: [
        'Background/Hook: General statement about the topic',
        'Thesis: Your main argument or position',
        'Roadmap (optional): Brief preview of what you\'ll discuss',
      ],
      examples: [
        {
          label: 'Opinion Essay Introduction',
          text: 'Technology has transformed modern education in unprecedented ways. While some argue this shift is beneficial, I firmly believe the advantages outweigh the disadvantages, as technology enhances both access to information and learning outcomes.',
        },
      ],
    },
    {
      title: 'Writing Body Paragraphs',
      content: `<p>Each body paragraph should follow the PEEL structure:</p>`,
      points: [
        '<strong>P</strong>oint: State your main idea (topic sentence)',
        '<strong>E</strong>xplain: Elaborate on this point',
        '<strong>E</strong>vidence: Provide examples or data',
        '<strong>L</strong>ink: Connect back to your thesis or transition to next point',
      ],
      examples: [
        {
          label: 'PEEL Example',
          text: 'Firstly, technology provides unprecedented access to educational resources. (Point) Students can now access university lectures, academic papers, and learning materials from anywhere in the world. (Explain) For instance, platforms like Coursera and Khan Academy offer free courses to millions of learners globally. (Evidence) This democratization of education directly supports the argument that technology benefits learning. (Link)',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Essay Types',
      type: 'select',
      instructions: 'What type of essay does each question require?',
      questions: [
        {
          text: '"Some people believe education should be free. To what extent do you agree or disagree?"',
          options: ['Opinion', 'Discussion', 'Problem/Solution'],
          answer: 'Opinion',
          explanation: '"To what extent do you agree" = opinion essay.',
        },
        {
          text: '"What are the advantages and disadvantages of remote work?"',
          options: ['Advantages/Disadvantages', 'Opinion', 'Discussion'],
          answer: 'Advantages/Disadvantages',
          explanation: 'Directly asks for pros and cons.',
        },
        {
          text: '"Discuss both views and give your own opinion."',
          options: ['Discussion', 'Opinion', 'Problem/Solution'],
          answer: 'Discussion',
          explanation: 'Requires discussing both sides before your opinion.',
        },
        {
          text: '"What problems does urbanization cause? What solutions can you suggest?"',
          options: ['Problem/Solution', 'Opinion', 'Advantages/Disadvantages'],
          answer: 'Problem/Solution',
          explanation: 'Directly asks for problems and solutions.',
        },
        {
          text: '"Do you think the advantages of X outweigh the disadvantages?"',
          options: ['Advantages/Disadvantages', 'Opinion', 'Discussion'],
          answer: 'Advantages/Disadvantages',
          explanation: 'Requires weighing pros and cons.',
        },
      ],
    },
    {
      title: 'Identify PEEL Components',
      type: 'select',
      instructions: 'What part of PEEL is each sentence?',
      questions: [
        {
          text: '"One significant benefit of technology is increased efficiency."',
          options: ['Point', 'Explain', 'Evidence', 'Link'],
          answer: 'Point',
          explanation: 'This is the topic sentence stating the main idea.',
        },
        {
          text: '"For instance, companies using automated systems report 40% faster processing times."',
          options: ['Evidence', 'Point', 'Explain', 'Link'],
          answer: 'Evidence',
          explanation: 'This provides a specific example/statistic.',
        },
        {
          text: '"This means that workers can complete more tasks in less time, reducing operational costs."',
          options: ['Explain', 'Point', 'Evidence', 'Link'],
          answer: 'Explain',
          explanation: 'This elaborates on the main point.',
        },
        {
          text: '"This clearly demonstrates how technology contributes to business success."',
          options: ['Link', 'Point', 'Explain', 'Evidence'],
          answer: 'Link',
          explanation: 'This connects back to the thesis/argument.',
        },
      ],
    },
    {
      title: 'Order the Introduction',
      type: 'select',
      instructions: 'Put these introduction sentences in the correct order.',
      questions: [
        {
          text: 'Which should come FIRST in an introduction?',
          options: [
            'General background statement',
            'Your thesis/opinion',
            'Specific example',
          ],
          answer: 'General background statement',
          explanation: 'Start broad, then narrow to your thesis.',
        },
        {
          text: 'Which should come LAST in an introduction?',
          options: [
            'Your thesis/main argument',
            'Background information',
            'Definition of key terms',
          ],
          answer: 'Your thesis/main argument',
          explanation: 'End with your clear position.',
        },
      ],
    },
    {
      title: 'Write Topic Sentences',
      type: 'writing',
      instructions: 'Write a topic sentence for each paragraph focus.',
      questions: [
        {
          text: 'Body paragraph about technology benefiting education',
          example: 'One significant advantage of technology in education is the increased accessibility it provides to learners worldwide.',
          tip: 'Start with "One significant...", "A key benefit...", or "Firstly..."',
        },
        {
          text: 'Body paragraph about drawbacks of technology in education',
          example: 'However, excessive reliance on technology can potentially undermine traditional learning skills and human interaction.',
          tip: 'Use transition words like "However" or "On the other hand".',
        },
        {
          text: 'Body paragraph about a solution to environmental problems',
          example: 'A practical solution to reducing pollution is implementing stricter regulations on industrial emissions.',
          tip: 'For problem/solution essays, state the solution clearly upfront.',
        },
      ],
    },
    {
      title: 'Complete the Essay Outline',
      type: 'fill-in',
      instructions: 'What should each paragraph contain?',
      questions: [
        {
          text: 'Introduction (2-3 sentences): Background + ___',
          answer: 'Thesis',
          explanation: 'Your thesis states your main argument or position.',
        },
        {
          text: 'Body paragraph structure: Point + Explain + Evidence + ___',
          answer: 'Link',
          explanation: 'Link connects back to your thesis.',
        },
        {
          text: 'Conclusion (2-3 sentences): Restate thesis + ___',
          answer: 'Final thought',
          explanation: 'End with a prediction, recommendation, or call to action.',
        },
      ],
    },
  ],
  tips: [
    'Plan your essay structure before writing',
    'Each body paragraph should have ONE main idea',
    'Use clear topic sentences to guide the reader',
    'Aim for 250-280 words total',
  ],
  dailyPractice: [
    'Practice writing introductions in 5 minutes',
    'Write body paragraphs using PEEL structure',
    'Time yourself: complete essays in 40 minutes',
    'Read Band 9 essays to understand ideal structure',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Essay Structure: Organizing IELTS Task 2 essays effectively using the 4-paragraph structure, PEEL paragraphs, and appropriate introduction/conclusion techniques.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...essayStructureData,
      title: essayStructureData.title + ' (Foundations)',
      overview: {
        ...essayStructureData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: essayStructureData.sections.slice(0, Math.max(2, Math.ceil(essayStructureData.sections.length / 2))),
      exercises: essayStructureData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...essayStructureData,
      title: essayStructureData.title + ' (Advanced)',
      overview: {
        ...essayStructureData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: essayStructureData.sections,
      exercises: essayStructureData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(essayStructureData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return essayStructureData;
}
